import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Box, CircularProgress, Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { superuserAuthService } from '../../services/superuserAuth'

interface SuperuserProtectedRouteProps {
  children: React.ReactNode
  requiredPermissions?: string[]
}

export const SuperuserProtectedRoute: React.FC<SuperuserProtectedRouteProps> = ({ 
  children, 
  requiredPermissions = [] 
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [hasPermissions, setHasPermissions] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    checkAuthentication()
  }, [])

  const checkAuthentication = async () => {
    try {
      // Check if user is authenticated
      const authenticated = superuserAuthService.isAuthenticated()
      
      if (!authenticated) {
        // Try to refresh token
        const refreshed = await superuserAuthService.refreshToken()
        if (!refreshed) {
          setIsAuthenticated(false)
          setIsLoading(false)
          return
        }
      }

      setIsAuthenticated(true)

      // Check permissions if required
      if (requiredPermissions.length > 0) {
        const hasAllPermissions = requiredPermissions.every(permission =>
          superuserAuthService.hasPermission(permission)
        )
        setHasPermissions(hasAllPermissions)
        
        if (!hasAllPermissions) {
          setError('You do not have the required permissions to access this page.')
        }
      } else {
        setHasPermissions(true)
      }
    } catch (error) {
      console.error('Authentication check failed:', error)
      setIsAuthenticated(false)
      setError('Authentication check failed. Please try logging in again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={40} />
        <Box sx={{ textAlign: 'center' }}>
          {t('auth.verifyingAuth')}
        </Box>
      </Box>
    )
  }

  // Redirect to superuser login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/platform/auth/login" replace />
  }

  // Show permission error if user doesn't have required permissions
  if (!hasPermissions) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error || t('auth.accessDenied')}
        </Alert>
      </Box>
    )
  }

  return <>{children}</>
}

export default SuperuserProtectedRoute